import { useEffect } from 'react'
import * as THREE from 'three'
import { PCDLoader } from 'three/examples/jsm/loaders/PCDLoader.js'

const stateStyles = {
  idle: { color: 0xd4b978, size: 0.0042 },
  listening: { color: 0x4dd4d9, size: 0.0054 },
  reading: { color: 0x9d8fff, size: 0.0048 },
  thinking: { color: 0x9d8fff, size: 0.0038 },
  speaking: { color: 0xd4b978, size: 0.0058 },
  success: { color: 0x5be6a0, size: 0.0052 },
  concerned: { color: 0xfbbf24, size: 0.0045 },
}

export function useAvatarThree(loading, avatarApiRef) {
  useEffect(() => {
    if (loading) return undefined
    const canvas = document.getElementById('avatarCanvas')
    const loaderText = document.querySelector('.model-loader .pct')
    const loaderWrap = document.querySelector('.model-loader')
    if (!canvas) return undefined

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 40)
    camera.position.set(0, 0, 2.8)

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      premultipliedAlpha: false,
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.setClearColor(0x000000, 0)
    renderer.setClearAlpha(0)
    canvas.style.background = 'transparent'

    const rig = new THREE.Group()
    scene.add(rig)

    let cloud = null
    let baseSize = 0.0042
    let avatarState = 'idle'
    let rotX = 0
    let rotY = 0
    let targetRotX = 0
    let targetRotY = 0
    let targetY = 0
    let targetScale = 1
    let stateLeanX = 0
    const clock = new THREE.Clock()

    const resize = () => {
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      if (!width || !height) return
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height, false)
    }

    const fitCloud = (points) => {
      points.geometry.center()
      points.geometry.rotateX(Math.PI)
      const box = new THREE.Box3().setFromObject(points)
      const size = box.getSize(new THREE.Vector3())
      points.scale.setScalar(1.6 / Math.max(size.x, size.y, size.z))
    }

    const applyAvatarState = (nextState) => {
      avatarState = nextState
      const sty = stateStyles[nextState] || stateStyles.idle
      if (cloud?.material) {
        cloud.material.color.setHex(sty.color)
        baseSize = sty.size
      }
      switch (nextState) {
        case 'listening': stateLeanX = 0.08; targetY = 0.02; targetScale = 1.02; break
        case 'reading': stateLeanX = 0.04; targetY = 0.01; targetScale = 1.01; break
        case 'thinking': stateLeanX = -0.03; targetY = -0.01; targetScale = 0.98; break
        case 'speaking': stateLeanX = 0.05; targetY = 0; targetScale = 1.03; break
        case 'success': stateLeanX = 0; targetY = 0.03; targetScale = 1.04; break
        case 'concerned': stateLeanX = -0.06; targetY = -0.02; targetScale = 0.97; break
        default: stateLeanX = 0; targetY = 0; targetScale = 1
      }
    }

    const setMouse = (nx, ny) => {
      targetRotY = nx * Math.PI * 0.55
      targetRotX = ny * Math.PI * 0.16
    }

    avatarApiRef.current = { setState: applyAvatarState, setMouse }

    const addFallbackCloud = () => {
      const geometry = new THREE.SphereGeometry(0.62, 38, 38)
      const source = geometry.getAttribute('position')
      const vertices = []
      for (let i = 0; i < source.count; i += 1) {
        vertices.push(new THREE.Vector3(source.getX(i), source.getY(i), source.getZ(i)))
      }
      const pointsGeometry = new THREE.BufferGeometry().setFromPoints(vertices)
      const pointsMaterial = new THREE.PointsMaterial({ color: stateStyles.idle.color, size: baseSize, sizeAttenuation: true })
      cloud = new THREE.Points(pointsGeometry, pointsMaterial)
      rig.add(cloud)
      if (loaderWrap) loaderWrap.style.display = 'none'
    }

    const loadCloud = async () => {
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('PCD load timeout')), 8000)
          new PCDLoader().load(
            `/avatar.pcd?v=${Date.now()}`,
            (points) => {
              try {
                fitCloud(points)
                if (!points?.material) throw new Error('PCD has no material')
                points.material.size = baseSize
                points.material.sizeAttenuation = true
                if (!points.material.vertexColors) points.material.color.setHex(stateStyles.idle.color)
                cloud = points
                rig.add(points)
                if (loaderWrap) loaderWrap.style.display = 'none'
                clearTimeout(timeout)
                resolve()
              } catch (callbackError) {
                clearTimeout(timeout)
                reject(callbackError)
              }
            },
            (xhr) => {
              if (!loaderText) return
              if (xhr.total) loaderText.textContent = `${Math.round((xhr.loaded / xhr.total) * 100)}%`
              else loaderText.textContent = `${Math.round((xhr.loaded || 0) / 1024)} KB`
            },
            (err) => {
              clearTimeout(timeout)
              reject(err)
            }
          )
        })
      } catch (err) {
        if (loaderText) loaderText.textContent = `fallback: ${String(err?.message || 'parse failed').slice(0, 42)}`
        addFallbackCloud()
      }
    }
    loadCloud()

    let frameId = null
    const animate = () => {
      frameId = window.requestAnimationFrame(animate)
      const dt = Math.min(clock.getDelta(), 0.05)
      const t = clock.elapsedTime
      const damp = reduce ? 1 : 1 - Math.exp(-6.5 * dt)
      if (cloud?.material) {
        let size = baseSize
        if (avatarState === 'speaking') size += Math.abs(Math.sin(t * 9)) * 0.0016
        else if (avatarState === 'listening') size += Math.sin(t * 2.5) * 0.0005
        cloud.material.size = size
      }
      rotX += (targetRotX + stateLeanX - rotX) * damp
      rotY += (targetRotY - rotY) * damp
      rig.rotation.x = rotX
      rig.rotation.y = rotY
      const breathe = Math.sin(t * 1.1) * 0.008
      const bob = avatarState === 'speaking' ? Math.sin(t * 7) * 0.01 : 0
      rig.position.y += (targetY + breathe + bob - rig.position.y) * damp
      rig.scale.setScalar(rig.scale.x + (targetScale - rig.scale.x) * damp)
      renderer.render(scene, camera)
    }

    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas.parentElement)
    resize()
    animate()

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      renderer.dispose()
    }
  }, [loading, avatarApiRef])
}
